import { Card, Flex, Heading, Wrap, Text, Button, Link } from '@chakra-ui/react';
import { toaster, useMobileContext, useValidatedSorting } from '@shared';
import { useTranslation } from 'react-i18next';
import { createDataCollectionContext, dbTimeToUserTime } from '@shared';
import { useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import inboundNumberReservationsService from '../../../../api/services/inboundNumberReservationsService';
import { useNavigate } from 'react-router';
import { InboundNumberDrawer } from './InboundNumberDrawer';
import { useDrawer } from '@admin';
import { IoCopyOutline } from 'react-icons/io5';
import { LuExternalLink } from 'react-icons/lu';

const columnHelper = createColumnHelper<InboundNumberReservation>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function InboundNumbers() {
	const { t } = useTranslation();
	const { isMobile } = useMobileContext();
	const navigate = useNavigate();
	const { openDrawer } = useDrawer();
	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter } = useMemo(() => createDataCollectionContext<InboundNumberReservation>(), []);

	const columns = [
		columnHelper.accessor('inboundNumber.phone', {
			header: t('general.phone'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('deliveryReportUrl', {
			header: t('general.delivery_report_url'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('webhookSecret', {
			header: t('general.webhook_secret'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				const secret = row.original.webhookSecret;
				if (!secret) {
					return '';
				}
				const copyToClipboard = () => {
					if (secret) {
						navigator.clipboard.writeText(secret);
						toaster.create({
							type: 'success',
							title: t('general.copied_to_clipboard'),
						});
					}
				};

				return (
					<Flex
						onClick={e => {
							e.stopPropagation();
							copyToClipboard();
						}}
						gap={2}
						alignItems={'center'}
						cursor={'copy'}
					>
						<IoCopyOutline size="20" />
						<Text style={{ cursor: 'copy', userSelect: 'text' }}>{secret}</Text>
					</Flex>
				);
			},
		}),
		columnHelper.accessor('timeStart', {
			header: t('general.time_active_start'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeStart) {
					return null;
				}
				return <Text>{dbTimeToUserTime(row.original.timeStart, TIMEZONE)}</Text>;
			},
		}),
		columnHelper.accessor('timeEnd', {
			header: t('general.time_active_end'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeEnd) {
					return null;
				}
				return <Text>{dbTimeToUserTime(row.original.timeEnd, TIMEZONE)}</Text>;
			},
		}),
	];

	useValidatedSorting('inboundNumbers', columns);

	return (
		<Wrap gap={4} width={'100%'}>
			<Card.Root maxWidth={'2000px'} width="70%" flex={1} minWidth={'340px'}>
				<Card.Body flexWrap={'wrap'} display={'flex'} flexDirection={'row'} justifyContent={'space-between'} padding={5} gap={5}>
					<Flex display={'flex'} flexDirection={'column'} gap={2} flex={1} maxWidth={'100%'}>
						<DataCollectionContextProvider
							dataKey="inboundNumberReservations"
							keyAttribute="inboundNumberReservationID"
							emptyDataText={t('general.no_inbound_numbers_found')}
							fetchFn={(...args) => inboundNumberReservationsService.getAllResources(...args)}
							queryParams={{ getRelated: 'inboundNumber' }}
							initialItemsPerPage={25}
							columns={columns}
							isServerSide={false}
							isSelectable={false}
							onClickRow={row => {
								openDrawer({
									id: 'inboundNumberDrawer',
									content: <InboundNumberDrawer inboundNumberID={row.original.inboundNumberID} />,
								});
							}}
						>
							<DataCollectionTable />
							<Flex gap={2}>
								<DataCollectionTableFooter />
							</Flex>
						</DataCollectionContextProvider>
					</Flex>
				</Card.Body>
			</Card.Root>
			<Card.Root height="fit-content" width={isMobile ? '100%' : '30%'} display={'flex'} flexDirection={'column'} gap={3}>
				<Card.Body flexWrap={'wrap'} display={'flex'} flexDirection={'row'} justifyContent={'space-between'} padding={5} gap={5}>
					<Flex display={'flex'} flexDirection={'column'} gap={2} flex={1} maxWidth={'2000px'}>
						<Heading size="md" marginBottom={2}>
							{t('general.inbound_numbers')}
						</Heading>
						<Text>{t('general.contact_customer_service_to_order_inbound_number')}</Text>
						<Text>
							<Link
								textDecor={'underline'}
								color="rgb(61, 213, 152)"
								href="https://prospectum.atlassian.net/wiki/spaces/TAVP/pages/3582001155/Tavoittaja+inbound+messages+public"
								target="_blank"
							>
								{t('general.inbound_number_documentation')}
								<LuExternalLink />
							</Link>
						</Text>
						<Button onClick={() => navigate('/customer-service?tab=contactCustomerSupport')} marginTop={4}>
							{t('general.contact_us_to_order')}
						</Button>
					</Flex>
				</Card.Body>
			</Card.Root>
		</Wrap>
	);
}
